import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Card, Table, Typography, Tag, Spin, Row, Col, Select, Space } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { reducer, sliceKey, actions } from "./slice";
import { samplingTrackingDetailedSaga } from "./saga";
import { 
  selectTableData, 
  selectTotal, 
  selectLoading, 
  selectPagination 
} from "./selectors";

const { Title, Text } = Typography;
const { Option } = Select;

const columns = [
  { 
    title: "#", 
    dataIndex: "serialNumber", 
    key: "serialNumber", 
    width: 60 
  },
  {
    title: "Sampler & Location",
    key: "sampler",
    width: 150,
    render: (_, record) => (
      <div>
        <Text strong>{record.samplerName}</Text>
        <div>
          {record.state} • {record.district}
        </div>
      </div>
    ),
  },
  {
    title: "Total Orders",
    dataIndex: "totalOrders",
    key: "totalOrders",
    width: 90,
  },
    {
    title: "Pending Orders",
    dataIndex: "pending",
    key: "pending",
    width: 100,
  },
  {
    title: "Collection Initiated",
    dataIndex: "collectionInitiated",
    key: "collectionInitiated",
    width: 100,
  },
  {
    title: "In-Review",
    dataIndex: "inReview",
    key: "inReview",
    width: 100,
    render: (val) => <Tag color="blue">{val}</Tag>,
  },
  {
    title: "Delivered to lab",
    dataIndex: "deliveredToLab",
    key: "deliveredToLab",
    width: 130,
  },
  {
    title: "Under Testing",
    dataIndex: "underTesting",
    key: "underTesting",
    width: 130,
  },
  {
    title: "Report Uploaded",
    dataIndex: "reportUploaded",
    key: "reportUploaded",
    width: 130,
  },
   {
    title: "Under Dispute",
    dataIndex: "underDispute",
    key: "underDispute",
    width: 130,
  },
   {
    title: "Completed",
    dataIndex: "samplingCompleted",
    key: "samplingCompleted",
    width: 130,
  },
  {
    title: "Under Correction",
    dataIndex: "needCorrection",
    key: "needCorrection",
    width: 130,
    render: (val) => <Tag color="orange">{val}</Tag>,
  },
  {
    title: "Not Found",
    dataIndex: "notFound",
    key: "notFound",
    width: 100,
    render: (val) => <Tag>{val}</Tag>,
  },
  {
    title: "Regret",
    dataIndex: "regret",
    key: "regret",
    width: 100,
    render: (val) => <Tag color="red">{val}</Tag>,
  },
  {
    title: "Invoice Value",
    dataIndex: "totalInvoiceValue",
    key: "totalInvoiceValue",
    width: 100,
    render: (val) => (
      <Text style={{ color: "green" }}>₹{val?.toLocaleString()}</Text>
    ),
  },
  {
    title: "Sampling Completion %",
    dataIndex: "samplingCompletionScore",
    key: "samplingCompletionScore",
    width: 130,
    //render: (val) => <Tag color="black">{val}</Tag>,
  },
  {
    title: "Completion %",
    dataIndex: "completionScore",
    key: "completionScore",
    width: 130,
    render: (val) => <Tag color="black">{val}</Tag>,
  },
];

const SamplingTrackingDetailed = ({ filters = {} }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({ key: sliceKey, saga: samplingTrackingDetailedSaga });

  const loading = useSelector(selectLoading);
  const data = useSelector(selectTableData);
  const total = useSelector(selectTotal);
  const pagination = useSelector(selectPagination);

  // State for filter values - initialize from location.state filters
  const [selectedClients, setSelectedClients] = useState(
    filters?.companyIds || []
  );
  const [selectedProjects, setSelectedProjects] = useState(
    filters?.projectCodeIds || []
  );

  // Memoize the filters string to prevent unnecessary re-renders
  const filtersString = useMemo(() => {
    return JSON.stringify({
      selectedClients,
      selectedProjects,
      ...filters,
    });
  }, [selectedClients, selectedProjects, filters]);

  useEffect(() => {
    setSelectedClients(filters?.companyIds || []);
    setSelectedProjects(filters?.projectCodeIds || []);
  }, [filters]);

  // Handle pagination changes
  const handleTableChange = useCallback((page, pageSize) => {
    dispatch(actions.setPagination({ currentPage: page, pageSize }));
  }, [dispatch]);

  const handlePageSizeChange = useCallback((newPageSize) => {
    dispatch(actions.setPageSize(newPageSize));
  }, [dispatch]);

  // Reset pagination when filters change
  useEffect(() => {
    dispatch(actions.resetPagination());
  }, [dispatch, filtersString]);

  // Fetch data effect - only fetch when filters change (not on pagination change)
  useEffect(() => {
    dispatch(
      actions.fetchDetailed({
        filtersString,
      })
    );
  }, [dispatch, filtersString]);

  // Memoize the table data with proper client-side pagination
  const tableData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    
    // Calculate pagination for client-side data
    const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    
    // Slice the data for current page
    const pageData = data.slice(startIndex, endIndex);
    
    // Add serial numbers based on actual position in full dataset
    return pageData.map((item, idx) => {
      const serialNumber = startIndex + idx + 1;
      return {
        ...item,
        key: serialNumber,
        serialNumber: serialNumber,
      };
    });
  }, [data, pagination.currentPage, pagination.pageSize]);

  // Memoize the back navigation handler
  const handleBackNavigation = useCallback(() => {
    history.push("/bo/dashboard", {
      filters: {
        selectedClients,
        selectedProjects,
        ...filters,
      },
    });
  }, [history, selectedClients, selectedProjects, filters]);

  // Calculate display range for current page
  const displayRange = useMemo(() => {
    const totalRecords = Array.isArray(data) ? data.length : 0;
    const startIndex = (pagination.currentPage - 1) * pagination.pageSize + 1;
    const endIndex = Math.min(pagination.currentPage * pagination.pageSize, totalRecords);
    return { startIndex, endIndex, totalRecords };
  }, [data, pagination.currentPage, pagination.pageSize]);

  // Pagination configuration for Ant Design Table
  const paginationConfig = {
    current: pagination.currentPage,
    pageSize: pagination.pageSize,
    total: Array.isArray(data) ? data.length : 0, // Use actual data length for total
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => 
      `${displayRange.startIndex}-${displayRange.endIndex} of ${displayRange.totalRecords} samplers`,
    pageSizeOptions: ['10', '30', '50', '100'],
    onChange: handleTableChange,
    onShowSizeChange: handleTableChange,
  };

  return (
    <div>
      <Card
        style={{ marginTop: 24 }}
        bodyStyle={{ padding: 0, backgroundColor: "#0D9488" }}
      >
        {/* Header with pagination controls */}
        <Row align="middle" style={{ padding: "16px 24px" }}>
          <Col>
            <ArrowLeftOutlined
              style={{
                fontSize: 18,
                color: "#fff",
                marginRight: 12,
                cursor: "pointer",
              }}
              onClick={handleBackNavigation}
            />
          </Col>
          <Col flex="auto">
            <Title level={4} style={{ color: "#fff", margin: 0 }}>
              Complete Sampling Tracking
            </Title>
          </Col>
          <Col>
            <Space align="center">
              <Text style={{ color: "#fff", fontSize: 14 }}>Show:</Text>
              <Select
                value={pagination.pageSize}
                onChange={handlePageSizeChange}
                style={{ width: 80 }}
                size="small"
              >
                <Option value={10}>10</Option>
                <Option value={30}>30</Option>
                <Option value={50}>50</Option>
                <Option value={100}>100</Option>
              </Select>
              <Text style={{ color: "#fff", fontSize: 14 }}>entries</Text>
            </Space>
          </Col>
        </Row>

        {/* Main Content */}
        <div style={{ padding: 24, backgroundColor: "#fff" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "50px" }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                Loading sampling tracking data...
              </div>
            </div>
          ) : (
            <div>
              {/* Summary Info */}
              <div style={{ marginBottom: 16 }}>
                <Title level={5}>
                  Complete Sampler Tracking ({total} Samplers)
                </Title>
                <Text style={{ color: "#666" }}>
                  Showing {displayRange.startIndex}-{displayRange.endIndex} of {total} samplers
                </Text>
              </div>

              <Table
                columns={columns}
                dataSource={tableData}
                pagination={paginationConfig}
                bordered
                scroll={{ x: "max-content", y: 400 }}
                rowKey="key"
              />
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default SamplingTrackingDetailed;